/**
 * Collapse sibling color products to one visible card per style family.
 *
 * Implements Agent F's frontend dedupe step from the Nosto product card plan
 * (§ "Style-Family Dedupe Design"):
 *
 * - Dedupe by `styleFamilyKey`, preserving the first Nosto-ranked hit as the
 *   representative for each family. Nosto's relevance ordering is the source
 *   of truth for which sibling represents the family.
 * - If `styleFamilyKey` is missing/empty for a hit, do not dedupe that hit —
 *   each keyless product stays as its own visible card. This matches the plan
 *   rule "If no confirmed key exists for a hit, do not dedupe that hit." and
 *   keeps the grid resilient when Nosto has not indexed the custom field for
 *   every product.
 * - Order of the returned array matches the input order with later duplicates
 *   removed. No re-ranking is applied; Liquid still owns the visual swatches
 *   inside each rendered card via the existing
 *   `product.metafields.custom.style.value.products.value` chain.
 *
 * The helper is intentionally generic over a `T extends StyleFamilyHit` so it
 * works with the decorated `Product` type from `@/types`, but also with any
 * minimal shape used in tests or mocks. The input array is not mutated.
 */
export type StyleFamilyHit = {
  styleFamilyKey?: string
}

export function dedupeByStyleFamily<T extends StyleFamilyHit>(hits: readonly T[] | undefined | null): T[] {
  if (!hits || hits.length === 0) {
    return []
  }

  const seen = new Set<string>()
  const result: T[] = []

  for (const hit of hits) {
    const key = hit?.styleFamilyKey
    if (typeof key !== "string" || key.length === 0) {
      result.push(hit)
      continue
    }

    if (seen.has(key)) {
      continue
    }

    seen.add(key)
    result.push(hit)
  }

  return result
}
